import React from 'react';
import { useNavigate } from 'react-router-dom';
import user_logo from 'assets/icons/user.svg';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale'; // Import Japanese locale
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

interface Vehicles {
  id: string;
  plate_number: string;
  car_model: string;
  last_used_by: {
    name: string;
    phone: string;
    email: string;
    profile_picture?: string;
  };
  create_time: Date;
  last_inspection: Date;
}

interface AllVehicleTableProps {
  vehicles: Vehicles[];
  onEdit: (vehicleId: string) => void;
  onDelete: (vehicleId: string) => void;
}

const AllVehicleTable: React.FC<AllVehicleTableProps> = ({
  vehicles,
  onEdit,
  onDelete,
}) => {
  const { t, i18n } = useTranslation('vehicle');
  const navigate = useNavigate();

  const checkDate = (initialDate: Date) => {
    if (format(new Date(initialDate), 'yyyy-MM-dd') === '1970-01-01')
      return '-';

    // Format the date based on the current i18n language
    if (i18n.language === 'jp') {
      return format(new Date(initialDate), 'yyyy/MM/dd', { locale: ja });
    } else {
      return format(new Date(initialDate), 'dd MMM yyyy');
    }
  };

  const handleRowClick = (id: string) => {
    navigate(`/vehicle/user/${id}`);
  };

  return (
    <main className="flex flex-col my-6">
      <div className="grid grid-cols-12 gap-4 px-2 py-3 bg-primary/50 rounded-md w-full">
        <div className="col-span-2">{t('all-vehicle.table.plate-number')}</div>
        <div className="col-span-3">{t('all-vehicle.table.car-model')}</div>
        <div className="col-span-2">{t('all-vehicle.table.last-used-by')}</div>
        <div className="col-span-2 text-center">
          {t('all-vehicle.table.added-date')}
        </div>
        <div className="col-span-2 text-center">
          {t('all-vehicle.table.last-inspection')}
        </div>
        <div className="col-span-1 text-center">
          {t('all-vehicle.table.action')}
        </div>
      </div>
      {vehicles.length === 0 ? (
        <div className="text-center my-4">{t('table.no-data')}</div>
      ) : (
        vehicles.map((vehicle, index) => (
          <div
            key={index}
            className="grid grid-cols-12 gap-4 px-2 py-3 border-b cursor-pointer"
            onClick={() => handleRowClick(vehicle.id)}
          >
            <div className="col-span-2 flex items-center text-neutral/100 truncate">
              {vehicle.plate_number}
            </div>
            <div className="col-span-3 flex items-center truncate">
              {vehicle.car_model}
            </div>
            {vehicle.last_used_by ? (
              <div className="col-span-2 flex space-x-2 items-center truncate">
                <img
                  src={vehicle.last_used_by?.profile_picture || user_logo}
                  alt="User"
                  className="h-10 w-10 object-cover rounded-full"
                />
                <div>
                  <div className="w-32">
                    {vehicle.last_used_by?.name || t('table.no-data')}
                  </div>
                  <div className="text-neutral/100 w-32">
                    {vehicle.last_used_by?.phone || vehicle.last_used_by?.email}
                  </div>
                </div>
              </div>
            ) : (
              <div className="col-span-2 flex items-center">---</div>
            )}
            <div className="col-span-2 flex items-center justify-center text-center">
              {checkDate(vehicle.create_time)}
            </div>
            <div className="col-span-2 flex items-center justify-center text-center">
              {checkDate(vehicle.last_inspection)}
            </div>
            <div className="col-span-1 flex items-center justify-center gap-3 cursor-pointer text-neutral/100">
              <IconEdit
                size={20}
                stroke={1.5}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(vehicle.id);
                }}
              />
              <IconTrash
                size={20}
                stroke={1.5}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(vehicle.id);
                }}
              />
            </div>
          </div>
        ))
      )}
    </main>
  );
};

export default AllVehicleTable;
